import { Link } from 'react-router-dom';
import { LegalHero, Lang, useLang, CONTACT_EMAIL } from './LegalLayout';

// 特定商取引法に基づく表記
// 設計メモ: 事業者名・住所・電話番号は、請求があれば遅滞なく開示する旨の記載でも可（個人事業の場合）。

type Row = [string, string];
type Doc = { eyebrow: string; title: string; lead: string; rows: Row[]; contactLink: string; dates: string[] };

const DOCS: Record<Lang, Doc> = {
  ja: {
    eyebrow: 'Legal',
    title: '特定商取引法に基づく表記',
    lead: '特定商取引に関する法律第11条に基づき、以下のとおり表示します。',
    rows: [
      ['販売事業者', '開誠株式会社（運営施設：ステイドリームホテル）'],
      ['運営統括責任者', '李 燕'],
      ['所在地', '〒116-0014 東京都荒川区東日暮里3-35-3'],
      ['電話番号', 'ご請求があった場合には遅滞なく開示いたします。お問い合わせはメールまたはお問い合わせフォームにてお願いいたします。'],
      ['メールアドレス', CONTACT_EMAIL],
      ['販売価格', '各宿泊プランの予約画面に表示される金額（税込）'],
      ['商品代金以外の必要料金', '宿泊税その他の公租公課が別途発生する場合があります。銀行振込の場合の振込手数料はお客様のご負担となります。'],
      ['お支払い方法', 'クレジットカード（Visa / Mastercard / JCB / American Express）、Apple Pay、Google Pay ほか予約画面に表示される方法'],
      ['お支払い時期', 'クレジットカード等：ご予約時に決済されます。銀行振込：ご案内する振込期限までにお振込みください。'],
      ['サービスの提供時期', 'ご予約いただいた宿泊日（チェックイン 16:00 以降／チェックアウト 10:00 まで）'],
      ['予約の成立', '決済完了時（銀行振込の場合は当施設が入金を確認した時点）に成立します。'],
      ['キャンセル・返金', 'チェックイン30日前までのキャンセルは全額返金いたします。それ以降のキャンセル・不泊は返金できません。詳しくは利用規約のキャンセルポリシーをご確認ください。'],
      ['動作環境', '最新版の主要ブラウザ（Chrome / Safari / Edge / Firefox）'],
    ],
    contactLink: 'お問い合わせフォーム',
    dates: ['制定日：2026年11月4日'],
  },
  en: {
    eyebrow: 'Legal',
    title: 'Notation Based on the Specified Commercial Transactions Act',
    lead: 'The following information is provided in accordance with Article 11 of the Act on Specified Commercial Transactions of Japan.',
    rows: [
      ['Seller', '開誠株式会社 (operator of Stay Dream Hotel)'],
      ['Responsible Manager', 'Li Yan (李 燕)'],
      ['Address', '3-35-3 Higashi-Nippori, Arakawa-ku, Tokyo 116-0014, Japan'],
      ['Phone', 'Will be disclosed without delay upon request. Please contact us by email or via the contact form.'],
      ['Email', CONTACT_EMAIL],
      ['Price', 'The amount (tax included) shown on the booking screen for each stay.'],
      ['Additional Fees', 'Accommodation tax and other public charges may apply separately. Bank transfer fees are borne by the guest.'],
      ['Payment Methods', 'Credit cards (Visa / Mastercard / JCB / American Express), Apple Pay, Google Pay and other methods shown on the booking screen.'],
      ['Payment Timing', 'Card payments: charged at the time of booking. Bank transfer: by the deadline stated in our instructions.'],
      ['Service Delivery', 'On the booked dates (check-in from 16:00, check-out by 10:00).'],
      ['Booking Confirmation', 'A booking is confirmed upon completion of payment (for bank transfers, when we confirm receipt).'],
      ['Cancellation & Refunds', 'Full refund for cancellations up to 30 days before check-in. No refund thereafter or for no-shows. See the cancellation policy in our Terms of Service.'],
      ['System Requirements', 'Latest versions of major browsers (Chrome / Safari / Edge / Firefox).'],
    ],
    contactLink: 'Contact form',
    dates: ['Established: November 4, 2026'],
  },
  zh: {
    eyebrow: 'Legal',
    title: '基于《特定商业交易法》的标示',
    lead: '根据日本《特定商业交易相关法律》第11条，标示如下。',
    rows: [
      ['销售经营者', '開誠株式会社（运营设施：梦幻住宿酒店）'],
      ['运营负责人', '李燕'],
      ['所在地', '〒116-0014 日本东京都荒川区东日暮里3-35-3'],
      ['电话号码', '如有需要，将在收到请求后及时提供。请通过电子邮件或咨询表单与我们联系。'],
      ['电子邮箱', CONTACT_EMAIL],
      ['销售价格', '以各住宿方案预订页面显示的金额（含税）为准。'],
      ['商品价款以外的必要费用', '可能另行产生住宿税等公共税费。银行转账的手续费由客人承担。'],
      ['支付方式', '信用卡（Visa / Mastercard / JCB / American Express）、Apple Pay、Google Pay 及预订页面显示的其他方式。'],
      ['支付时间', '信用卡等：预订时扣款。银行转账：请于指定的转账期限前汇款。'],
      ['服务提供时间', '所预订的住宿日期（16:00 后入住／10:00 前退房）。'],
      ['预订成立', '支付完成时（银行转账则为本设施确认到账时）成立。'],
      ['取消与退款', '入住30天前取消可全额退款。此后取消或未入住不予退款。详情请参阅服务条款中的取消政策。'],
      ['运行环境', '主流浏览器最新版本（Chrome / Safari / Edge / Firefox）。'],
    ],
    contactLink: '咨询表单',
    dates: ['制定日期：2026年11月4日'],
  },
};

function TokushohoPage() {
  const lang = useLang();
  const d = DOCS[lang];
  return (
    <div className="legal-page">
      <LegalHero eyebrow={d.eyebrow} title={d.title} lead={d.lead} />
      <section className="legal-body">
        <div className="legal-body-inner">
          <table className="legal-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {d.rows.map(([k, v]) => (
                <tr key={k} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', verticalAlign: 'top', padding: '0.9rem 1rem 0.9rem 0', width: '32%', fontWeight: 600 }}>{k}</th>
                  <td style={{ padding: '0.9rem 0', lineHeight: 1.7 }}>
                    {k === 'メールアドレス' || k === 'Email' || k === '电子邮箱'
                      ? <><a href={`mailto:${v}`}>{v}</a>（<Link to="/contact">{d.contactLink}</Link>）</>
                      : v}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="legal-dates">
            {d.dates.map((x, i) => <p key={i}>{x}</p>)}
          </div>
        </div>
      </section>
    </div>
  );
}

export default TokushohoPage;
